import { FetchArgs, createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { piiConfig, searchFields } from "../../../../../config/constants";
import { IGraphGetDriveInfoFromSharepointIdRequest, IGraphGetUsersRequest, IGraphSearchSharePointResponse } from "./api-graph-interfaces";

const GRAPH_API_URL = 'https://graph.microsoft.com/v1.0';

const staggeredBaseQueryWithBailOut = retry(
    async (args: string | FetchArgs, api, extraOptions) => {
        const result = await fetchBaseQuery({
            baseUrl: GRAPH_API_URL,
            prepareHeaders: async (headers) => {
                return headers;
            }
        })(args, api, extraOptions)
        const errorStatus = result?.error?.status;
        if (errorStatus === 401 || errorStatus === 403) {
            retry.fail(result.error);
        }
        return result;
    },
    {
        maxRetries: 3,
    }
);

export const apiGraphSlice = createApi({
    reducerPath: 'api-graph',
    baseQuery: staggeredBaseQueryWithBailOut,
    endpoints: (builder) => ({
        getMetaDataFields: builder.query<any, { driveId: string, itemId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { driveId, itemId } = args;
                return {
                    url: `/drives/${driveId}/items/${itemId}/listItem?$select=sharepointIds&$expand=fields($select=${piiConfig.privacySentitiveColumnName},${piiConfig.privacySentitiveDataColumnName})`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getUsers: builder.query<any, { request: IGraphGetUsersRequest, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('ConsistencyLevel', `eventual`);

                return {
                    url: `/users?$count=true&$search="displayName:${args.request.filter}" OR "mail:${args.request.filter}" OR "userPrincipalName:${args.request.filter}"&$orderBy=${args.request.orderby}&$select=${args.request.select}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getAllUsers: builder.query<any, { request: IGraphGetUsersRequest, accessToken: string, top?: number }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('ConsistencyLevel', `eventual`);

                return {
                    url: `/users?$top=${args.top ? args.top : 10}&$orderBy=${args.request.orderby}&$select=${args.request.select}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getPeople: builder.query<any, { request: IGraphGetUsersRequest, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('ConsistencyLevel', `eventual`);

                //url: `/me/people?$top30&$search=${args.request.filter}&$select=displayName,scoredEmailAddresses,personType,department,jobTitle,userPrincipalName&$filter=personType/subclass eq 'OrganizationUser' or personType/subclass eq 'Guest' or personType/subclass eq 'UnifiedGroup'`,
                return {
                    url: `/people?$count=true&$filter=startsWith(displayName,'${args.request.filter}') or startsWith(userPrincipalName,'${args.request.filter}') or startsWith(mail,'${args.request.filter}')&$orderBy=${args.request.orderby}&$select=${args.request.select}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getGroups: builder.query<any, { request: IGraphGetUsersRequest, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('ConsistencyLevel', `eventual`);

                return {
                    url: `/groups?$count=true&$filter=startsWith(displayName,'${args.request.filter}') or startsWith(mail,'${args.request.filter}')&$orderBy=${args.request.orderby}&$select=${args.request.select}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getDriveInfoFromSharepointId: builder.query<any, { request: IGraphGetDriveInfoFromSharepointIdRequest, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/drives/${args.request.driveId}/root/search(q='${args.request.sharepointId}')`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getItemWebUrl: builder.query<any, { driveId: string, itemId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/drives/${args.driveId}/items/${args.itemId}?$select=id,webUrl&$expand=listItem($select=webUrl)`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getItem: builder.query<any, { driveId: string, itemId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/drives/${args.driveId}/items/${args.itemId}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getRecentFiles: builder.query<any, { accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/me/drive/recent?$select=remoteItem&$top=500`,
                    method: 'GET',
                    headers
                }
            },
            transformResponse: (response: any) => {
                const newResponse = [];

                response.value.forEach((item) => {
                    newResponse.push({
                        ...item.remoteItem,
                        col_name: item.remoteItem.name,
                        col_lastModifiedDateTime: item.remoteItem.lastModifiedDateTime,
                        col_size: item.remoteItem.size,
                        col_lastModifiedBy: item.remoteItem.lastModifiedBy.user.displayName,
                    })

                });

                return newResponse;
            },
        }),
        searchSharePoint: builder.query<IGraphSearchSharePointResponse, { searchValue: string, searchFilter?: string, siteUrlList?: string[], from?: number, size?: number, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { searchValue, searchFilter, siteUrlList, from, size } = args;

                let queryString: string = searchFilter
                    ? `(*${searchValue}*) ${searchFilter}`
                    : `(*${searchValue}*)`;

                if (Array.isArray(siteUrlList) && siteUrlList.length > 0) {
                    const siteUrls: string[] = siteUrlList.filter(siteUrl => siteUrl);
                    if (siteUrls.length > 0) {
                        queryString += ` (${siteUrlList
                            .map(siteUrl => `site:\"${siteUrl}\"`)
                            .join(" OR ")})`;
                    }
                }

                const body = {
                    requests: [
                        {
                            entityTypes: [
                                "site",
                                "driveItem"
                            ],
                            query: {
                                queryString
                            },
                            fields: searchFields.graph,
                            from: from ?? 0,
                            size: size ?? 250
                        }
                    ]
                }

                return {
                    url: `/search/query`,
                    method: 'POST',
                    body,
                    headers
                }
            },
            transformResponse: (response: any) => {
                return { ...response.value[0].hitsContainers[0] } as IGraphSearchSharePointResponse;
            },
        }),
        getSiteIdFromDriveId: builder.query<any, { driveId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/drives/${args.driveId}?$select=list&$expand=list($select=parentReference)`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getDriveItemBySharingUrl: builder.query<any, { sharingUrl: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { sharingUrl } = args;

                let base64Value = window.btoa(sharingUrl) as string;
                let encodedUrl = `u!${base64Value.replace(new RegExp("=" + "([^" + "=" + "]*)$"), "" + "$1")}`; //Replace last = from base64 string
                encodedUrl = encodedUrl.replaceAll("/", "_");
                encodedUrl = encodedUrl.replaceAll("+", "-");

                return {
                    url: `https://graph.microsoft.com/v1.0/shares/${encodedUrl}/driveItem?$select=name,sharepointIds,parentReference&$expand=listItem`,
                    method: 'GET',
                    headers
                }
            }
        }),
        sendMail: builder.query<any, { accessToken: string, html: string, subject: string, toRecipients: string[] }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { html, subject, toRecipients } = args;

                const body = {
                    "message": {
                        "subject": subject,
                        "body": {
                            "contentType": "HTML",
                            "content": html
                        },
                        "toRecipients": toRecipients.map(recipient => {
                            return {
                                "emailAddress": {
                                    "address": recipient
                                }
                            }
                        }),
                    }
                }

                return {
                    url: `https://graph.microsoft.com/v1.0/me/sendMail`,
                    method: 'POST',
                    body,
                    headers
                }
            }
        })
    })
});

export const {
    useLazyGetMetaDataFieldsQuery,
    useLazyGetUsersQuery,
    useLazyGetAllUsersQuery,
    useLazyGetPeopleQuery,
    useLazyGetGroupsQuery,
    useLazyGetDriveInfoFromSharepointIdQuery,
    useLazyGetRecentFilesQuery,
    useLazySearchSharePointQuery,
    useLazyGetItemWebUrlQuery,
    useLazyGetSiteIdFromDriveIdQuery,
    useLazyGetItemQuery,
    useLazyGetDriveItemBySharingUrlQuery,
    useLazySendMailQuery
} = apiGraphSlice;